import {
  EmptyState,
  PageIntro,
  PanelHeader,
} from './components.js';
import type {
  SeasonRosterAssignedEntry,
  SeasonRosterAvailableEntry,
  SeasonRosterWorkspaceView,
} from './seasonRosterWorkspace.js';

interface SeasonRosterWorkspaceSectionProps {
  searchTerm: string;
  workspace: SeasonRosterWorkspaceView;
  hasPendingChanges: boolean;
  savePending: boolean;
  onSearchTermChange: (value: string) => void;
  onResetChanges: () => void;
  onSaveChanges: () => void;
  onToggleRemoval: (playerId: string) => void;
  onUndoAdd: (playerId: string) => void;
  onAdd: (playerId: string) => void;
}

export function SeasonRosterWorkspaceSection({
  searchTerm,
  workspace,
  hasPendingChanges,
  savePending,
  onSearchTermChange,
  onResetChanges,
  onSaveChanges,
  onToggleRemoval,
  onUndoAdd,
  onAdd,
}: SeasonRosterWorkspaceSectionProps) {
  const saveDisabled = !hasPendingChanges || savePending;

  return (
    <>
      <PageIntro
        title="Season Roster"
        actions={
          <div className="inline-actions">
            <button
              type="button"
              className="secondary"
              disabled={!hasPendingChanges || savePending}
              onClick={onResetChanges}
            >
              Reset Changes
            </button>
            <button
              type="button"
              disabled={saveDisabled}
              onClick={onSaveChanges}
            >
              {savePending
                ? 'Saving...'
                : hasPendingChanges
                  ? `Save ${workspace.pendingChangeCount} change${workspace.pendingChangeCount === 1 ? '' : 's'}`
                  : 'Save'}
            </button>
          </div>
        }
      />
      <section className="panel panel-accent stack">
        <PanelHeader
          title="Roster Workspace"
        />
        <div className="row">
          <div className="field">
            <label htmlFor="season-roster-search">Search members</label>
            <input
              id="season-roster-search"
              placeholder="Search by player name or email"
              value={searchTerm}
              onChange={(event) => onSearchTermChange(event.target.value)}
            />
          </div>
        </div>
      </section>
      <section className="grid two season-roster-grid">
        <article className="panel">
          <PanelHeader
            title={`Assigned to Season (${workspace.assigned.length})`}
          />
          {workspace.assigned.length === 0 ? (
            <EmptyState title="No season players yet" message="Stage league members into the season from the available list to begin recording scores." />
          ) : (
            <div className="card-list roster-card-list">
              {workspace.assigned.map((entry) => (
                <SeasonRosterAssignedCard
                  key={entry.playerId}
                  entry={entry}
                  saveDisabled={saveDisabled}
                  savePending={savePending}
                  onSaveChanges={onSaveChanges}
                  onToggleRemoval={() => onToggleRemoval(entry.playerId)}
                  onUndoAdd={() => onUndoAdd(entry.playerId)}
                />
              ))}
            </div>
          )}
        </article>

        <article className="panel">
          <PanelHeader
            title={`Available League Members (${workspace.available.length})`}
          />
          {workspace.available.length === 0 ? (
            <EmptyState
              title={searchTerm ? 'No matching league members' : 'No available members'}
              message={
                searchTerm
                  ? 'Try a different search term.'
                  : 'All active league members are already assigned to this season.'
              }
            />
          ) : (
            <div className="card-list roster-card-list">
              {workspace.available.map((entry) => (
                <SeasonRosterAvailableCard
                  key={entry.playerId}
                  entry={entry}
                  onAdd={() => onAdd(entry.playerId)}
                />
              ))}
            </div>
          )}
        </article>
      </section>
    </>
  );
}

function SeasonRosterAssignedCard({
  entry,
  onToggleRemoval,
  onUndoAdd,
  onSaveChanges,
  saveDisabled,
  savePending,
}: {
  entry: SeasonRosterAssignedEntry;
  onToggleRemoval: () => void;
  onUndoAdd: () => void;
  onSaveChanges: () => void;
  saveDisabled: boolean;
  savePending: boolean;
}) {
  const statusLabel =
    entry.source === 'pending-add' ? 'Will Add' : entry.pendingRemoval ? 'Will Remove' : 'In Season';

  return (
    <div className={`card roster-workspace-card ${entry.pendingRemoval ? 'roster-workspace-card-muted' : ''}`}>
      <div className="roster-workspace-card-header">
        <div>
          <strong>{entry.name}</strong>
          <div className="muted roster-workspace-meta">
            {entry.email || 'No email'} | {entry.role}
          </div>
        </div>
        <span className={`status-pill ${entry.source === 'pending-add' ? 'status-pill-accent' : ''}`}>
          {statusLabel}
        </span>
      </div>
      <div className="row">
        <div className="field">
          <label>League Starting Handicap</label>
          <div className="season-roster-note-block">{entry.startingHandicap?.toFixed(1) ?? 'Carried from league membership'}</div>
          <p className="muted">Season assignment preserves the league handicap.</p>
        </div>
        <div className="field">
          <label>Current Handicap Index</label>
          <div className="season-roster-note-block">
            {entry.currentHandicapIndex !== null ? entry.currentHandicapIndex.toFixed(1) : 'Carried from league membership'}
          </div>
        </div>
      </div>
      <div className="inline-actions">
        {entry.source === 'pending-add' ? (
          <>
            <button type="button" className="secondary" onClick={onUndoAdd}>
              Undo Add
            </button>
            <button type="button" disabled={saveDisabled} onClick={onSaveChanges}>
              {savePending ? 'Saving...' : 'Save All Changes'}
            </button>
          </>
        ) : (
          <button type="button" className={entry.pendingRemoval ? 'secondary' : 'danger'} onClick={onToggleRemoval}>
            {entry.pendingRemoval ? 'Keep in Season' : 'Remove from Season'}
          </button>
        )}
      </div>
    </div>
  );
}

function SeasonRosterAvailableCard({
  entry,
  onAdd,
}: {
  entry: SeasonRosterAvailableEntry;
  onAdd: () => void;
}) {
  return (
    <div className="card roster-workspace-card">
      <div className="roster-workspace-card-header">
        <div>
          <strong>{entry.name}</strong>
          <div className="muted roster-workspace-meta">
            {entry.email || 'No email'} | {entry.role}
          </div>
        </div>
        <span className="status-pill">Available</span>
      </div>
      <div className="inline-actions">
        <button type="button" onClick={onAdd}>
          Add to Season
        </button>
      </div>
    </div>
  );
}
